import React, { useEffect, useMemo, useState, useRef } from "react";
import {
  Box,
  Grid,
  GridItem,
  FormControl,
  FormLabel,
  Input,
  Textarea,
  Button,
  Heading,
  useToast,
  Flex,
  FormErrorMessage,
  Spinner,
  Center,
} from "@chakra-ui/react";

import Select from "react-select";
import { useNavigate, useLocation } from "react-router-dom";
import { FormHeader } from "../../../ui/FormHeader";
import { Trash2, X, GripVertical } from "lucide-react";
import { FaChevronDown, FaCloudUploadAlt, FaFileUpload, FaImage, FaPaperclip, FaTrash } from "react-icons/fa";
import { addShade, updateShade, getShadeDetailById, deleteShadeItemsById, addShadeinZohoBooks } from "../../../redux/api/admin/shadeApi";
import { addShadeAction, updateShadeAction } from "../../../redux/reduxSlice/admin/ShadesSlice";
import { useDispatch, useSelector } from "react-redux";
import { getCategoryList } from "../../../redux/reduxApi/admin/CategoryReduxApi";
import { getProduct } from "../../../redux/reduxApi/admin/ProductReduxApi";
import { getItemsByCategory } from "../../../redux/api/admin/InventoryApi";
import { isTypeRequiredOptions, itemUnitOptions } from "../utils/SearchPickList";
import { getTags } from "../../../redux/reduxApi/admin/TagReduxApi";
import { getWarehouseList } from "../../../redux/reduxApi/admin/WarehouseReduxApi";
import useProductSearch from "../../../hook/useProductSearch";
import useTagSearch from "../../../hook/useTagsSearch";
import { AsyncPaginate } from "react-select-async-paginate";
// Created By / Modified By ke liye - ClientForm jaisa hi pattern
import { getEmployeesList } from "../../../redux/reduxApi/admin/EmployeesReduxApi";
import { getLoggedInUserId } from "../../../../utils/authUtils";
import { getUserDetails } from "../../../../utils/IndexDB/PermissionStore";

// Helper: Zoho Catalyst lookup columns (createdBy/modifiedBy) must always be sent
// as a plain ROWID string, never as the hydrated {firstName, lastName, ROWID} object.
// Sending the object causes: "Invalid Foreign key value for column createdBy.
// ROWID of table users is expected"
const extractRowId = (value) => {
  if (value && typeof value === "object") {
    return value.ROWID || value.rowId || value.value || "";
  }
  return value || "";
};

export default function ShadesForm() {
  const navigate = useNavigate();
  const location = useLocation();
  const toast = useToast();
  const { loadProducts } = useProductSearch();
  const { loadTags } = useTagSearch();
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [loading, setLoading] = useState(false);
  // dragOverKey stores a composite "type-index" string so that the ring
  // highlight is scoped to the correct list (existing vs new).
  const [dragOverKey, setDragOverKey] = useState(null);
  const [dragItemType, setDragItemType] = useState(null);
  const [draggedImageIndex, setDraggedImageIndex] = useState(null);
  const fileInputRef = useRef(null);
  const [imagesPopupOpen, setImagesPopupOpen] = useState(false);
  const imagesPopupRef = useRef(null);
  const paperclipButtonRef = useRef(null);

  // State for category-specific items
  const [categoryItems, setCategoryItems] = useState([]);
  const [loadingCategoryItems, setLoadingCategoryItems] = useState(false);

  // State for warehouse data
  const [warehouseData, setWarehouseData] = useState([]);
  const [currentPage] = useState(1);

  // Warehouse rows state
  const [warehouseRows, setWarehouseRows] = useState([
    { warehouseName: "", openingStock: "", openingStockValuePerUnit: "" }
  ]);

  const categoryList = useSelector((state) => state.category.category);
  const [categoryOptions, setCategoryOptions] = useState([]);
  const dispatch = useDispatch();
  const isUpdateMode = location.state?.isUpdateMode;
  const shadeId = location.state?.id;
  const getTagsData = useSelector((state) => state?.tags?.tags);

  // Created By / Modified By state - ClientForm jaisa hi
  const loggedInUserId = getLoggedInUserId();
  const [userDetails, setUserDetails] = useState(null);
  const [createdByOptions, setCreatedByOptions] = useState([]);

  const [formData, setFormData] = useState({
    shadeName: "",
    shadeNumber: "",
    shadeCategory: null,
    maxWidth: "",
    fabric: "",
    gsm: "",
    uvBlockage: "",
    opennessFactor: "",
    greenGuard: "",
    description: "",
    isTypeRequired: "",
    items: [{ productId: "" }],
    itemImages: [],
    newImages: [],
    removeImages: [],
    imgRowId: [],
    isPrintingRequired: false,
    tagsId: [],
    // New fields
    hscCode: "",
    unit: "",
    availableStock: "",
    sellingPrice: "",
    costPrice: "",
    sellingAccount: "",
    purchaseAccount: "",
    sellingDescription: "",
    purchaseDescription: "",
    createdBy: null,
    modifiedBy: "",
  });

  // Fetch warehouse data
  useEffect(() => {
    dispatch(getWarehouseList({ page: currentPage, size: 30 }))
      .then((res) => {
        console.log("warehouse res", res?.payload?.data?.data);
        setWarehouseData(res?.payload?.data?.data || []);
      })
      .catch((error) => {
        console.error("Error fetching Warehouse data:", error);
      });
  }, [currentPage, dispatch]);

  // Fetch logged-in user details (for Created By display)
  useEffect(() => {
    const fetchUserDetails = async () => {
      const user = await getUserDetails();
      setUserDetails(user);
    };
    fetchUserDetails();
  }, []);

  // ---- FIX: createdBy must be a USERS table ROWID, not an EMPLOYEES table ROWID ----
  // getLoggedInUserId() reads localStorage "loggedEmployee" -> employees table ROWID.
  // getUserDetails() reads IndexDB (populated at login from the users/auth record)
  // -> userDetails.ROWID is the correct users table ROWID for createdBy/modifiedBy.
  // Only auto-fill for NEW shades; in update mode createdBy should stay whatever
  // was fetched from the existing shade record (handled in the edit-mode effect).
  useEffect(() => {
    if (userDetails?.ROWID && !isUpdateMode) {
      setFormData((prev) => ({
        ...prev,
        createdBy: prev.createdBy || userDetails.ROWID,
      }));
    }
  }, [userDetails, isUpdateMode]);
  // ---- END FIX ----

  // Fetch employees list (for Created By / Modified By dropdown options)
  useEffect(() => {
    dispatch(getEmployeesList({ page: 1, size: 30, limit: 30 }))
      .then((response) => {
        const employees = response.payload?.data?.employees || [];
        const options = employees.map((emp) => ({
          label: `${emp.firstName || ''} ${emp.lastName || ''}`.trim() || emp.email || 'Unknown',
          value: emp.ROWID,
        }));
        setCreatedByOptions(options);
      });
  }, [dispatch]);

  const loggedInUserOption = useMemo(() => {
    if (!loggedInUserId) return null;
    return {
      label: userDetails?.employeeName || "Current User",
      value: loggedInUserId,
    };
  }, [loggedInUserId, userDetails]);

  const selectedCreatedByOption = useMemo(() => {
    const targetCreatedBy = formData.createdBy || loggedInUserId;
    if (!targetCreatedBy) return null;

    const option = createdByOptions.find(
      (opt) => String(opt.value) === String(targetCreatedBy)
    );
    if (option) return option;

    if (loggedInUserOption && String(loggedInUserOption.value) === String(targetCreatedBy)) {
      return loggedInUserOption;
    }

    if (formData.createdByDetails) {
      const details = formData.createdByDetails;
      const fullName = `${details.firstName || ""} ${details.lastName || ""}`.trim();
      return {
        label: fullName || details.employeeName || details.email || "Unknown",
        value: details.ROWID,
      };
    }

    return null;
  }, [createdByOptions, formData.createdBy, formData.createdByDetails, loggedInUserId, loggedInUserOption]);

  const selectedModifiedByOption = useMemo(() => {
    const targetModifiedBy = formData.modifiedBy || loggedInUserId;
    if (!targetModifiedBy) return null;

    const option = createdByOptions.find(
      (opt) => String(opt.value) === String(targetModifiedBy)
    );
    if (option) return option;

    if (loggedInUserOption && String(loggedInUserOption.value) === String(targetModifiedBy)) {
      return loggedInUserOption;
    }

    if (formData.modifiedByDetails) {
      const details = formData.modifiedByDetails;
      const fullName = `${details.firstName || ""} ${details.lastName || ""}`.trim();
      return {
        label: fullName || details.employeeName || details.email || "Unknown",
        value: details.ROWID,
      };
    }

    return null;
  }, [createdByOptions, formData.modifiedBy, formData.modifiedByDetails, loggedInUserId, loggedInUserOption]);

  const getTagsByIds = async (ids) => {
    try {
      const results = await Promise.all(
        ids.map((id) =>
          dispatch(
            getTags({
              page: 1,
              size: 1,
              search: [
                {
                  field: "ROWID",
                  operation: "is",
                  value: id,
                },
              ],
            })
          )
        )
      );

      return results
        .map((res) => {
          const tag = res.payload?.data?.tagMasters?.[0];
          if (!tag) return null;

          return {
            value: tag.tagMasters.ROWID,
            label: tag.tagMasters.tagName,
          };
        })
        .filter(Boolean);
    } catch (err) {
      console.error("Error fetching tags by ids:", err);
      return [];
    }
  };

  // Warehouse Name Options
  const warehouseNameOptions = warehouseData.map((w) => ({
    value: w.warehouseName,
    label: w.warehouseName,
  }));

  // Warehouse row handlers
  const handleAddWarehouseRow = () => {
    setWarehouseRows([
      ...warehouseRows,
      { warehouseName: "", openingStock: "", openingStockValuePerUnit: "" },
    ]);
  };

  const handleDeleteWarehouseRow = (index) => {
    const updatedRows = warehouseRows.filter((_, i) => i !== index);
    setWarehouseRows(updatedRows);
  };

  const handleWarehouseInputChange = (index, field, value) => {
    const updatedRows = [...warehouseRows];
    updatedRows[index][field] = value;
    setWarehouseRows(updatedRows);
  };

  // Track items to be deleted
  const [itemsToDelete, setItemsToDelete] = useState([]);
  const [originalItems, setOriginalItems] = useState([]);
  const [categoryData, setCategoryData] = useState([]);
  const [originalShadeMedia, setOriginalShadeMedia] = useState("");

  // Validation function
  const validateForm = () => {
    const newErrors = {};
    if (!formData.shadeNumber?.trim()) {
      newErrors.shadeNumber = "Shade Number is required";
    }
    if (!formData.shadeName?.trim()) {
      newErrors.shadeName = "Shade Name is required";
    }
    if (!formData.shadeCategory) {
      newErrors.shadeCategory = "Category is required";
    }
    if (!formData.maxWidth?.trim()) {
      newErrors.maxWidth = "Max Width is required";
    }
    if (typeof formData.isPrintingRequired !== 'boolean') {
      newErrors.isPrintingRequired = "Please select if printing is required";
    }
    if (!formData.fabric?.trim()) {
      newErrors.fabric = "Fabric is required";
    }

    // Numeric field validations
    if (formData.sellingPrice && isNaN(parseFloat(formData.sellingPrice))) {
      newErrors.sellingPrice = "Selling Price must be a number";
    }
    if (formData.costPrice && isNaN(parseFloat(formData.costPrice))) {
      newErrors.costPrice = "Cost Price must be a number";
    }
    if (formData.availableStock && isNaN(parseFloat(formData.availableStock))) {
      newErrors.availableStock = "Available Stock must be a number";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  useEffect(() => {
    dispatch(getCategoryList());
  }, [dispatch]);

  useEffect(() => {
    if (isUpdateMode && shadeId) {
      setLoading(true);
      getShadeDetailById(shadeId)
        .then((response) => {
          console.log("Shade API Response:", response);

          if (response.data && response.data.data.shade) {
            const shadeData = response.data.data.shade;
            const shadeItems = response.data.data.shade.items || [];
            setOriginalShadeMedia(shadeData.shadeMedia || "[]");

            // Parse shadeMedia from API response for display
            let parsedShadeMedia = [];
            if (shadeData.shadeMedia) {
              try {
                // Parse as JSON first
                if (typeof shadeData.shadeMedia === 'string') {
                  try {
                    const parsed = JSON.parse(shadeData.shadeMedia);

                    if (Array.isArray(parsed)) {
                      parsedShadeMedia = parsed
                        .filter(item => item && (typeof item === 'string' || item.url))
                        .map(item => {
                          // Handle string URLs
                          if (typeof item === 'string') {
                            return {
                              id: item.split('/').pop() || Date.now().toString(),
                              name: item.split('/').pop() || 'image',
                              url: item,
                              result: true
                            };
                          }
                          // Handle object format
                          return {
                            id: item.id || (item.url ? item.url.split('/').pop() : Date.now().toString()),
                            name: item.name || 'image',
                            size: item.size || '',
                            mimetype: item.mimetype || '',
                            url: item.url || '',
                            result: item.result ?? true
                          };
                        });
                    }
                  } catch {
                    // Not valid JSON - extract URLs using regex
                    const urlRegex = /https?:\/\/[^\s"'[\]]+/g;
                    const urls = shadeData.shadeMedia.match(urlRegex) || [];

                    parsedShadeMedia = urls
                      .filter(url => url.trim())
                      .map(url => ({
                        id: url.split('/').pop() || Date.now().toString(),
                        name: url.split('/').pop() || 'image',
                        url: url,
                        result: true
                      }));
                  }
                }
                // Handle if it's already an array
                else if (Array.isArray(shadeData.shadeMedia)) {
                  parsedShadeMedia = shadeData.shadeMedia
                    .filter(item => item)
                    .map(item => {
                      const url = typeof item === 'string' ? item : item.url;
                      return {
                        id: (typeof item === 'object' && item.id) || (url ? url.split('/').pop() : Date.now().toString()),
                        name: (typeof item === 'object' && item.name) || (url ? url.split('/').pop() : 'image'),
                        size: (typeof item === 'object' && item.size) || '',
                        mimetype: (typeof item === 'object' && item.mimetype) || '',
                        url: url || '',
                        result: (typeof item === 'object' && item.result) ?? true
                      };
                    });
                }
              } catch (error) {
                console.error("Error parsing shadeMedia:", error);
                parsedShadeMedia = [];
              }
            }

            // Filter out any items without valid URLs
            parsedShadeMedia = parsedShadeMedia.filter(item => item.url && item.url.trim());

            // Parse tagsId
            const parsedTags = JSON.parse(shadeData.tagsId || "[]");

            getTagsByIds(parsedTags).then((formattedTags) => {
              setFormData((prev) => ({
                ...prev,
                tagsId: formattedTags,
              }));
            });

            // Parse warehouse details
            let warehouseRowsData = [];
            if (shadeData.warehouseDetails && Array.isArray(shadeData.warehouseDetails)) {
              warehouseRowsData = shadeData.warehouseDetails.map(item => ({
                warehouseName: item.warehouseName || "",
                openingStock: item.openingStock?.toString() || "",
                openingStockValuePerUnit: item.openingStockValuePerUnit?.toString() || ""
              }));
            }
            if (warehouseRowsData.length === 0) {
              warehouseRowsData = [{ warehouseName: "", openingStock: "", openingStockValuePerUnit: "" }];
            }
            setWarehouseRows(warehouseRowsData);

            // Parse items from shadeItems array
            let itemsArray = [];
            if (shadeItems && Array.isArray(shadeItems)) {
              itemsArray = shadeItems.map(item => ({
                productId: item.ROWID || "",
              }));
              setOriginalItems(itemsArray.map(item => item.productId));
              if (itemsArray.length === 0) {
                itemsArray = [{ productId: "" }];
              }
            } else {
              itemsArray = [{ productId: "" }];
            }

            // ---- FIX: createdBy / modifiedBy lookup values from Zoho Catalyst ----
            // IMPORTANT PRIORITY ORDER: shadeData.CREATORID / MODIFIERID are Zoho
            // Catalyst's BUILT-IN system columns (who created/modified the record
            // via the Catalyst API) - they reference Catalyst's internal user
            // system, NOT our custom "users" table. Our custom createdBy/modifiedBy
            // lookup FIELDS are the ones that correctly point to the users table.
            // Using CREATORID/MODIFIERID first was the bug: it sent a
            // Catalyst-internal ROWID where a users-table ROWID was required,
            // causing "Invalid Foreign key value for column createdBy".
            // shadeData.createdBy / shadeData.modifiedBy can come back as a hydrated
            // lookup object ({firstName, lastName, ROWID, ...}) instead of a plain
            // ROWID string, so we still run it through extractRowId().
            const rawCreatedBy = shadeData.createdBy || shadeData.creator;
            const rawModifiedBy = shadeData.modifiedBy || shadeData.modifier;

            const resolvedCreatedById =
              extractRowId(rawCreatedBy) || shadeData.CREATORID || "";
            const resolvedModifiedById =
              extractRowId(rawModifiedBy) || shadeData.MODIFIERID || "";

            const resolvedCreatedByDetails =
              (rawCreatedBy && typeof rawCreatedBy === "object" ? rawCreatedBy : null) ||
              shadeData.createdByDetails ||
              null;
            const resolvedModifiedByDetails =
              (rawModifiedBy && typeof rawModifiedBy === "object" ? rawModifiedBy : null) ||
              shadeData.modifiedByDetails ||
              null;
            // ---- END FIX ----

            // Update form data with new fields
            setFormData({
              shadeName: shadeData.shadeName || "",
              shadeNumber: shadeData.shadeNo || "",
              shadeCategory: shadeData.shadeCategory?.ROWID || "",
              maxWidth: shadeData.maxWidth || "",
              fabric: shadeData.fabric || "",
              gsm: shadeData.gsm || "",
              uvBlockage: shadeData.uvBlockage || "",
              opennessFactor: shadeData.opennessFactor || "",
              greenGuard: shadeData.greenGuard || "",
              isPrintingRequired: shadeData.isPrintingRequired || false,
              description: shadeData.description || "",
              items: itemsArray,
              itemImages: parsedShadeMedia,
              newImages: [],
              removeImages: [],
              // New fields
              hscCode: shadeData.hscCode || "",
              unit: shadeData.unit || "",
              availableStock: shadeData.availableStock || "",
              sellingPrice: shadeData.sellingPrice || "",
              costPrice: shadeData.costPrice || "",
              sellingAccount: shadeData.sellingAccount || "",
              purchaseAccount: shadeData.purchaseAccount || "",
              sellingDescription: shadeData.sellingDescription || "",
              purchaseDescription: shadeData.purchaseDescription || "",
              // Created By / Modified By - ROWID string only, details kept separately
              createdBy: resolvedCreatedById,
              createdByDetails: resolvedCreatedByDetails,
              modifiedBy: resolvedModifiedById,
              modifiedByDetails: resolvedModifiedByDetails,
            });
          } else {
            toast({
              title: "No shade data found",
              status: "warning",
              duration: 3000,
              isClosable: true,
            });
          }
        })
        .catch((error) => {
          console.error("Error fetching shade details:", error);
          toast({
            title: "Error fetching shade details",
            description: error.message || "Please try again",
            status: "error",
            duration: 3000,
            isClosable: true,
          });
        })
        .finally(() => {
          setLoading(false);
        });
    }
  }, [isUpdateMode, shadeId, toast]);

  useEffect(() => {
    setLoading(true);
    dispatch(getCategoryList({ page: currentPage, size: 30, limit: 30 })).then((response) => {
      console.log("category-->", response.payload.data || []);
      setCategoryData(response.payload?.data || []);
    })
      .catch((error) => {
        setLoading(false);
        console.error("Error fetching Deal data:", error);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [currentPage, dispatch]);

  useEffect(() => {
    const options = categoryData
      .filter(item => item.parentCategoryId === null)
      .map(item => ({
        label: item.name,
        value: item.ROWID,
      }));
    setCategoryOptions(options);
  }, [categoryData]);


  // Handle select changes
  const handleSelectChange = async (name, selectedOption) => {
    const value = selectedOption ? selectedOption.value : "";
    const label = selectedOption ? selectedOption.label : "";

    setFormData(prev => ({
      ...prev,
      [name]: value,
      [`${name}Label`]: label
    }));

    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ""
      }));
    }

    if (name === "shadeCategory" && value) {
      setLoadingCategoryItems(true);
      try {
        const response = await getItemsByCategory({ categoryId: value });
        console.log("Category items response:", response);
        if (response?.data?.tasks) {
          setCategoryItems(response.data.tasks);
        } else {
          setCategoryItems([]);
        }
      } catch (error) {
        console.error("Error fetching category items:", error);
        setCategoryItems([]);
      } finally {
        setLoadingCategoryItems(false);
      }
    } else if (name === "shadeCategory" && !value) {
      setCategoryItems([]);
    }
  };

  // Handle input change
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  const compressImage = (file, maxSize) => {
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = (event) => {
        const img = new Image();
        img.src = event.target.result;
        img.onload = () => {
          const canvas = document.createElement("canvas");
          let width = img.width;
          let height = img.height;

          const maxWidth = 1920;
          if (width > maxWidth) {
            height = (height * maxWidth) / width;
            width = maxWidth;
          }

          canvas.width = width;
          canvas.height = height;

          const ctx = canvas.getContext("2d");
          ctx.drawImage(img, 0, 0, width, height);

          let quality = 0.9;
          const tryCompress = () => {
            canvas.toBlob(
              (blob) => {
                if (blob.size > maxSize && quality > 0.1) {
                  quality -= 0.1;
                  tryCompress();
                } else {
                  const compressedFile = new File([blob], file.name, {
                    type: file.type,
                    lastModified: Date.now(),
                  });
                  resolve(compressedFile);
                }
              },
              file.type,
              quality
            );
          };
          tryCompress();
        };
      };
    });
  };

  const handleImageUpload = async (e) => {
    const files = Array.from(e.target.files);
    console.log("image files", files);
    const imageFiles = files.filter((file) => file.type.startsWith("image/"));

    if (imageFiles.length !== files.length) {
      toast({
        title: "Only image files are allowed",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    const totalImages = formData.itemImages.length + formData.newImages.length + imageFiles.length;
    if (totalImages > 5) {
      toast({
        title: "You can only upload a maximum of 5 images",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    const maxSize = 3 * 1024 * 1024;

    const processedFiles = await Promise.all(
      imageFiles.map(async (file) => {
        if (file.size > maxSize) {
          toast({
            title: `Compressing ${file.name}...`,
            status: "info",
            duration: 2000,
            isClosable: true,
          });
          return await compressImage(file, maxSize);
        }
        return file;
      })
    );
    console.log("processedFiles------", processedFiles);

    const newFiles = processedFiles.map((file) => ({
      file,
      url: URL.createObjectURL(file),
      name: file.name,
      type: file.type,
    }));
    console.log("newFiles------", newFiles)

    setFormData((prev) => ({
      ...prev,
      newImages: [...prev.newImages, ...newFiles],
    }));

    console.log("form data", formData);
    toast({
      title: `${processedFiles.length} image${processedFiles.length > 1 ? "s" : ""} uploaded successfully`,
      status: "success",
      duration: 3000,
      isClosable: true,
    });
  };

  // Drag and drop handlers for images
  // Uses a composite key "type-index" so existing and new lists are independent.
  const handleDragStart = (e, index, type) => {
    e.dataTransfer.effectAllowed = 'move';
    setDraggedImageIndex(index);
    setDragItemType(type);
    e.dataTransfer.setData('text/plain', `${type}-${index}`);
  };

  const handleDragEnd = () => {
    // Always clean up state when a drag ends (even on miss/cancel)
    setDragOverKey(null);
    setDraggedImageIndex(null);
    setDragItemType(null);
  };

  const handleDragOver = (e, index, type) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    const key = `${type}-${index}`;
    if (dragOverKey !== key) {
      setDragOverKey(key);
    }
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setDragOverKey(null);
  };

  const handleDrop = (e, targetIndex, targetType) => {
    e.preventDefault();

    if (draggedImageIndex === null || dragItemType !== targetType || draggedImageIndex === targetIndex) {
      setDragOverKey(null);
      setDraggedImageIndex(null);
      setDragItemType(null);
      return;
    }

    if (dragItemType === 'existing' && targetType === 'existing') {
      // Reorder existing images
      const updatedImages = [...formData.itemImages];
      const [draggedImage] = updatedImages.splice(draggedImageIndex, 1);
      updatedImages.splice(targetIndex, 0, draggedImage);

      setFormData(prev => ({
        ...prev,
        itemImages: updatedImages
      }));
    } else if (dragItemType === 'new' && targetType === 'new') {
      // Reorder new images
      const updatedImages = [...formData.newImages];
      const [draggedImage] = updatedImages.splice(draggedImageIndex, 1);
      updatedImages.splice(targetIndex, 0, draggedImage);

      setFormData(prev => ({
        ...prev,
        newImages: updatedImages
      }));
    } else if (dragItemType === 'existing' && targetType === 'new') {
      toast({
        title: "Cannot move existing images to new uploads section",
        status: "warning",
        duration: 2000,
        isClosable: true,
      });
    } else if (dragItemType === 'new' && targetType === 'existing') {
      toast({
        title: "Cannot move new uploads to existing images section",
        status: "warning",
        duration: 2000,
        isClosable: true,
      });
    }

    setDragOverKey(null);
    setDraggedImageIndex(null);
    setDragItemType(null);
  };

  // Close images popup when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (!imagesPopupOpen) return;
      const popup = imagesPopupRef.current;
      const button = paperclipButtonRef.current;
      if (popup && !popup.contains(e.target) && button && !button.contains(e.target)) {
        setImagesPopupOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [imagesPopupOpen]);


  const handleRemoveImage = (index, type) => {
    console.log("handleRemoveImage called - index:", index, "type:", type);

    if (type === "existing") {
      const imageToRemove = formData.itemImages[index];
      const imageUrl = imageToRemove.url || imageToRemove;

      setFormData((prev) => ({
        ...prev,
        itemImages: prev.itemImages.filter((_, i) => i !== index),
        removeImages: [...prev.removeImages, imageUrl],
        imgRowId: [...(prev.imgRowId || []), imageUrl],
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        newImages: prev.newImages.filter((_, i) => i !== index),
      }));
    }
  };

  // Handle item selection change
  const handleItemChange = (index, selectedOption) => {
    const value = selectedOption ? selectedOption.value : "";
    setFormData((prev) => {
      const updated = [...prev.items];
      updated[index].productId = value;
      return { ...prev, items: updated };
    });

    if (errors.items) {
      setErrors((prev) => ({
        ...prev,
        items: "",
      }));
    }
  };

  const addNewRow = () => {
    setFormData((prev) => ({
      ...prev,
      items: [...prev.items, { productId: "" }],
    }));
  };

  const removeRow = (index) => {
    const itemToRemove = formData.items[index];

    if (isUpdateMode && shadeId && itemToRemove.productId) {
      if (originalItems.includes(itemToRemove.productId)) {
        setItemsToDelete(prev => [...prev, itemToRemove.productId]);
      }
    }

    setFormData((prev) => ({
      ...prev,
      items: prev.items.filter((_, i) => i !== index),
    }));
  };

  const deleteRemovedItems = async () => {
    if (itemsToDelete.length === 0) {
      return true;
    }

    try {
      const deletePayload = {
        productIds: itemsToDelete
      };
      await deleteShadeItemsById(shadeId, deletePayload);
      console.log("Successfully deleted items:", itemsToDelete);
      return true;
    } catch (error) {
      console.error("Error deleting items:", error);
      toast({
        title: "Error deleting items",
        description: error.message || "Failed to delete removed items",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
      return false;
    }
  };

  const totalImages = (formData.itemImages?.length || 0) + (formData.newImages?.length || 0);

  console.log("totalImages--------", totalImages);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (isSubmitting) return;

    if (!validateForm()) {
      toast({
        title: "Please fill in all required fields",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    if (!isUpdateMode && !userDetails?.ROWID) {
      toast({
        title: "Please wait",
        description: "User details are still loading, try again in a moment.",
        status: "warning",
        duration: 3000,
        isClosable: true,
      });
      return;
    }
    // ---- END FIX ----

    setIsSubmitting(true);

    try {
      if (isUpdateMode && shadeId && itemsToDelete.length > 0) {
        const deleteSuccess = await deleteRemovedItems();
        if (!deleteSuccess) {
          setIsSubmitting(false);
          return;
        }
      }

      const submitData = new FormData();

      // Add text fields
      submitData.append("shadeName", formData.shadeName);
      submitData.append("shadeNo", formData.shadeNumber);
      submitData.append("shadeCategory", formData.shadeCategory);
      submitData.append("maxWidth", formData.maxWidth);
      submitData.append("fabric", formData.fabric);
      submitData.append("gsm", formData.gsm);
      submitData.append("uvBlockage", formData.uvBlockage);
      submitData.append("opennessFactor", formData.opennessFactor);
      submitData.append("greenGuard", formData.greenGuard);
      submitData.append("isPrintingRequired", formData.isPrintingRequired);
      submitData.append("description", formData.description);
      submitData.append(
        "tagsId",
        JSON.stringify(formData.tagsId.map((tag) => tag.value))
      );

      // New fields
      submitData.append("hscCode", formData.hscCode || "");
      submitData.append("unit", formData.unit || "");
      submitData.append("availableStock", formData.availableStock || "");
      submitData.append("sellingPrice", formData.sellingPrice || "");
      submitData.append("costPrice", formData.costPrice || "");
      submitData.append("sellingAccount", formData.sellingAccount || "");
      submitData.append("purchaseAccount", formData.purchaseAccount || "");
      submitData.append("sellingDescription", formData.sellingDescription || "");
      submitData.append("purchaseDescription", formData.purchaseDescription || "");

      // ---- FIX: Created By / Modified By ----
      // createdBy/modifiedBy is a foreign key into the USERS table, so it must be
      // userDetails.ROWID (from IndexDB / getUserDetails()) - NOT loggedInUserId
      // (getLoggedInUserId(), which is an EMPLOYEES table ROWID and gets rejected
      // by the backend with "Invalid Foreign key value for column createdBy").
      // Also defensively unwrap formData.createdBy in case it ever holds an
      // object instead of a plain ROWID string.
      const createdByValue = extractRowId(formData.createdBy) || userDetails?.ROWID || "";
      const modifiedByValue = userDetails?.ROWID || "";

      submitData.append("createdBy", createdByValue);
      submitData.append("modifiedBy", modifiedByValue);
      // ---- END FIX ----

      // Only send items that have productId
      const validItems = formData.items.filter(item => item.productId && item.productId.trim() !== "");
      submitData.append("items", JSON.stringify(validItems));

      // Append warehouse details
      const warehouseDetails = warehouseRows
        .filter(row => row.warehouseName && row.openingStock !== "" && row.openingStockValuePerUnit !== "")
        .map(row => ({
          warehouseName: row.warehouseName,
          openingStock: Number(row.openingStock),
          openingStockValuePerUnit: Number(row.openingStockValuePerUnit)
        }));
      if (warehouseDetails.length > 0) {
        submitData.append("warehouseDetailsInserted", JSON.stringify(warehouseDetails));
      }

      let response;

      if (isUpdateMode && shadeId) {
        const hasImageChanges = formData.newImages.length > 0 || (formData.imgRowId && formData.imgRowId.length > 0);

        const orderedExistingImages = formData.itemImages
          .filter((image) => !(formData.imgRowId || []).includes(image.url))
          .map((image) => {
            return image.url || image;
          })
          .filter(url => url && url.trim() !== "");

        const mediaString = JSON.stringify(orderedExistingImages);

        submitData.append("existingMediaOrder", mediaString);

        if (formData.imgRowId && formData.imgRowId.length > 0) {
          submitData.append("imgRowId", JSON.stringify(formData.imgRowId));
          console.log("Deleted image URLs being sent:", formData.imgRowId);
        }
        // Append new image files under the "shadeMedia" multipart field
        formData.newImages.forEach((imageObj) => {
          submitData.append("shadeMedia", imageObj.file);
        });

        submitData.append("ROWID", shadeId);
        response = await updateShade(shadeId, submitData);
        dispatch(updateShadeAction({ ROWID: shadeId, ...formData }));

        toast({
          title: "Success",
          description: "Shade updated successfully",
          status: "success",
          duration: 3000,
          isClosable: true,
        });
      } else {

        formData.newImages.forEach((imageObj) => {
          submitData.append("shadeMedia", imageObj.file);
        });

        let response = await addShade(submitData);
        dispatch(addShadeAction(response?.data?.data));
        // if (response) {
        // const payload = {
        //     "type": "shade"
        //   }
        //   const res = await addShadeinZohoBooks(response?.data?.data?.shade?.ROWID , payload);
        //   console.log("zoho books client response", res)
        // }
        toast({
          title: "Success",
          description: "Shade created successfully",
          status: "success",
          duration: 3000,
          isClosable: true,
        });
      }

      navigate('/admin/inventory/shade-list');

    } catch (error) {
      console.error('Error saving shade:', error);

      if (error.response) {
        console.error('Error response data:', error.response.data);
        console.error('Error response status:', error.response.status);
      }

      toast({
        title: "Error",
        description: error.response?.data?.error || `Failed to ${isUpdateMode ? 'update' : 'create'} shade`,
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const [inventoryData, setInventoryData] = useState([]);
  const [inventoryAllData, setInventoryAllData] = useState([]);

  const loadMechanismOptions = async (search, loadedOptions, { page }) => {
    try {
      const response = await dispatch(
        getProduct({
          page,
          size: 30,
          search: search
            ? [
              {
                field: "itemName",
                operation: "like",
                value: search,
              },
            ]
            : [],
        })
      );

      const data = response?.payload?.inventory || [];

      const options = data.map((item) => ({
        label: item.itemName,
        value: item.ROWID,
      }));

      return {
        options,
        hasMore: data.length === 30,
        additional: {
          page: page + 1,
        },
      };
    } catch (error) {
      console.error("Error loading mechanism options:", error);
      return {
        options: [],
        hasMore: false,
        additional: { page },
      };
    }
  };

  const inventoryLookUp = useMemo(() => {
    if (!formData.shadeCategory) {
      return [];
    }
    const itemsToUse = categoryItems.length > 0 ? categoryItems : inventoryData;
    return itemsToUse?.map((item) => ({
      label: item?.itemName,
      value: item.ROWID,
    }));
  }, [categoryItems, inventoryData, formData.shadeCategory]);

  useEffect(() => {
    if (isUpdateMode && formData.shadeCategory && categoryItems.length === 0) {
      setLoadingCategoryItems(true);
      getItemsByCategory({ categoryId: formData.shadeCategory })
        .then((response) => {
          if (response?.data?.tasks) {
            setCategoryItems(response.data.tasks);
          }
        })
        .catch((error) => {
          console.error("Error fetching category items for update:", error);
        })
        .finally(() => {
          setLoadingCategoryItems(false);
        });
    }
  }, [isUpdateMode, formData.shadeCategory]);

  useEffect(() => {
    if (inventoryLookUp) {
      setInventoryAllData(inventoryLookUp);
    }
  }, [inventoryLookUp]);

  // Unit options from SearchPickList
  const unitOptions = itemUnitOptions || [];

  // Account options (similar to AddProductForm)
  const salesAccountOptions = [
    { value: "Sales Revenue", label: "Sales Revenue" },
    { value: "Sales Discount", label: "Sales Discount" },
    { value: "Sales Return", label: "Sales Return" },
  ];

  const purchaseAccountOptions = [
    { value: "Purchase Cost", label: "Purchase Cost" },
    { value: "Purchase Discount", label: "Purchase Discount" },
    { value: "Purchase Return", label: "Purchase Return" },
  ];

  return (
    <Box className="h-[93vh] flex flex-col">
      {loading && (
        <Center flex="1" flexDirection="column" gap={4}>
          <div className="spinner-overlay">
            <Spinner size="xl" color="#011638" />
          </div>
        </Center>
      )}

      {!loading && (
        <>
          {/* Header */}
          <Box className="sticky top-0 z-10 bg-white">
            <FormHeader
              heading="Shade"
              onSubmit={handleSubmit}
              isUpdateMode={isUpdateMode}
              isSubmitting={isSubmitting}
            />
          </Box>

          {/* Content */}
          <Box className="flex-1 overflow-y-auto p-4 md:p-6">
            <Box
              p={{ base: 4, md: 6 }}
              borderWidth="1px"
              borderRadius="lg"
              bg="white"
              boxShadow="sm"
            >
              <Heading size="md" mb={4}>
                Shade Information
              </Heading>

              {/* Grid Layout */}
              <div className="flex flex-col md:flex-row items-center md:items-start justify-between gap-7 w-full">
                <div className="w-full md:w-1/2">
                  <Grid templateColumns={{ base: "1fr" }} gap={4}>
                    <GridItem>
                      <FormControl isInvalid={errors.shadeName}>
                        <FormLabel>
                          Product Name<span style={{ color: "red" }}>*</span>
                        </FormLabel>
                        <AsyncPaginate
                          value={
                            formData.shadeName
                              ? { label: formData.shadeName, value: formData.shadeName }
                              : null
                          }
                          loadOptions={loadProducts}
                          additional={{ page: 1 }}
                          debounceTimeout={300}
                          isClearable
                          placeholder="Search Product Name"
                          onChange={(option) =>
                            handleSelectChange("shadeName", option)
                          }
                        />
                        <FormErrorMessage fontSize="sm">
                          {errors.shadeName}w
                        </FormErrorMessage>
                      </FormControl>
                    </GridItem>

                    <GridItem>
                      <FormControl isInvalid={errors.shadeNumber}>
                        <FormLabel>
                          Shade Number<span style={{ color: "red" }}>*</span>
                        </FormLabel>
                        <Input
                          name="shadeNumber"
                          placeholder="Shade Number"
                          value={formData.shadeNumber}
                          onChange={handleChange}
                        />
                        <FormErrorMessage fontSize="sm">
                          {errors.shadeNumber}
                        </FormErrorMessage>
                      </FormControl>
                    </GridItem>

                    <GridItem>
                      <FormControl isInvalid={errors.shadeCategory}>
                        <FormLabel>
                          Category<span style={{ color: "red" }}>*</span>
                        </FormLabel>
                        <Select
                          options={categoryOptions}
                          name="shadeCategory"
                          placeholder="Shade Category"
                          isSearchable={true}
                          isClearable
                          isDisabled={isSubmitting}
                          onChange={(option) =>
                            handleSelectChange("shadeCategory", option)
                          }
                          value={categoryOptions.find(
                            (option) =>
                              option.value === formData.shadeCategory ||
                              option.label === formData.shadeCategory,
                          )}
                          styles={{
                            control: (base, state) => ({
                              ...base,
                              cursor: "pointer",
                              borderColor: errors.shadeCategory
                                ? "#E53E3E"
                                : base.borderColor,
                              "&:hover": {
                                borderColor: errors.shadeCategory
                                  ? "#E53E3E"
                                  : base.borderColor,
                              },
                            }),
                            option: (base, state) => ({
                              ...base,
                              cursor: "pointer",
                            }),
                          }}
                        />
                        <FormErrorMessage fontSize="sm">
                          {errors.shadeCategory}
                        </FormErrorMessage>
                      </FormControl>
                    </GridItem>
                  </Grid>

                </div>

                <div className="w-full md:w-1/2">
                  <GridItem mb={3}>
                    <label>Media</label>
                    <Input
                      ref={fileInputRef}
                      type="file"
                      id="imageUpload"
                      multiple
                      accept="image/*"
                      className="hidden"
                      onChange={handleImageUpload}
                    />
                    {totalImages === 0 && (
                      <label
                        htmlFor="imageUpload"
                        className="flex flex-col justify-center items-center w-full h-[180px] border-2 border-dashed border-gray-300 rounded-lg bg-gray-50 cursor-pointer transition-all duration-200 hover:border-blue-900 hover:bg-blue-50"
                      >
                        <div className="w-[70px] h-[70px] bg-gradient-to-br from-blue-950 to-blue-900 rounded-xl flex justify-center items-center mb-3">
                          <FaFileUpload size={36} color="white" />
                        </div>
                        <div className="text-sm text-gray-700 mb-1">
                          <span className="text-blue-950 font-semibold">
                            Click to Upload Images
                          </span>
                        </div>
                        <div className="text-xs text-gray-500">
                          (You can upload a maximum of{" "}
                          <span className="font-bold text-blue-900">5</span>{" "}
                          images)
                        </div>
                      </label>
                    )}

                    {totalImages > 0 && (

                      <div className="flex gap-3 items-center mt-2">
                        {formData.itemImages.length > 0 && (
                          console.log("Rendering existing images:", formData.itemImages) ||
                          <div className="flex flex-wrap gap-2">
                            {formData.itemImages.map((image, index) => (
                              <div
                                key={image.id || image.url || index}
                                className={`relative group ${dragOverKey === `existing-${index}` ? 'ring-2 ring-blue-500 ring-offset-2' : ''}`}
                                draggable
                                onDragStart={(e) => handleDragStart(e, index, 'existing')}
                                onDragEnd={handleDragEnd}
                                onDragOver={(e) => handleDragOver(e, index, 'existing')}
                                onDragLeave={handleDragLeave}
                                onDrop={(e) => handleDrop(e, index, 'existing')}
                              >
                                <div className="relative">
                                  <img
                                    src={image.url}
                                    alt={image.name}
                                    className="w-16 h-16 object-cover rounded border cursor-move"
                                  />
                                  <div className="absolute top-1 left-1 bg-black bg-opacity-50 rounded p-0.5 cursor-grab active:cursor-grabbing">
                                    <GripVertical size={12} className="text-white" />
                                  </div>
                                </div>
                                <button
                                  type="button"
                                  onClick={() => {
                                    handleRemoveImage(index, "existing")
                                  }
                                  }
                                  className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                                  title="Remove image"
                                >
                                  <X />
                                </button>
                              </div>
                            ))}
                          </div>
                        )}

                        {formData.newImages.length > 0 && (
                          <div className="flex flex-wrap gap-2">
                            {formData.newImages.map((file, index) => (
                              <div
                                key={file.name + index}
                                className={`relative group ${dragOverKey === `new-${index}` ? 'ring-2 ring-blue-500 ring-offset-2' : ''}`}
                                draggable
                                onDragStart={(e) => handleDragStart(e, index, 'new')}
                                onDragEnd={handleDragEnd}
                                onDragOver={(e) => handleDragOver(e, index, 'new')}
                                onDragLeave={handleDragLeave}
                                onDrop={(e) => handleDrop(e, index, 'new')}
                              >
                                <div className="relative">
                                  <img
                                    src={file.url}
                                    alt={file.name}
                                    className="w-16 h-16 object-cover rounded border cursor-move"
                                  />
                                  <div className="absolute top-1 left-1 bg-black bg-opacity-50 rounded p-0.5 cursor-grab active:cursor-grabbing">
                                    <GripVertical size={12} className="text-white" />
                                  </div>
                                </div>
                                <button
                                  type="button"
                                  onClick={() =>
                                    handleRemoveImage(index, "new")
                                  }
                                  className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                                  title="Remove image"
                                >
                                  <X />
                                </button>
                              </div>
                            ))}
                          </div>
                        )}

                        <div className="relative">
                          <button
                            type="button"
                            ref={paperclipButtonRef}
                            className="flex items-center gap-2 px-2 py-1.5 text-lg font-bold text-white bg-blue-950 rounded-lg"
                            onClick={() => setImagesPopupOpen((p) => !p)}
                          >
                            <FaPaperclip size={16} />
                            <span>{totalImages}</span>
                          </button>

                          <div
                            ref={imagesPopupRef}
                            className={`absolute top-full left-[-190px] mt-2 w-[400px] bg-white rounded-lg shadow-lg border border-gray-200 z-10 ${imagesPopupOpen ? "" : "hidden"}`}
                          >
                            <div className="flex justify-between px-4 py-2 border-b">
                              <span className="font-semibold">
                                Uploaded Images ({totalImages}/5)
                              </span>
                              <button onClick={() => setImagesPopupOpen(false)}>
                                ×
                              </button>
                            </div>
                            <div className="max-h-[300px] overflow-y-auto p-2">
                              {formData.itemImages.map((image, index) => (
                                <div
                                  key={image.id || index}
                                  className="flex items-center gap-3 py-2 px-2 hover:bg-gray-50 border-b"
                                >
                                  <FaImage size={18} className="text-blue-900" />
                                  <span className="flex-1 truncate text-sm">
                                    {image.name}
                                  </span>
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      e.preventDefault();
                                      handleRemoveImage(index, "existing")
                                    }

                                    }
                                    className="p-2 text-red-600 hover:bg-red-50 rounded"
                                    title="Remove image"
                                  >
                                    <FaTrash size={14} />
                                  </button>
                                </div>
                              ))}
                              {formData.newImages.map((file, index) => (
                                <div
                                  key={index}
                                  className="flex items-center gap-3 py-2 px-2 hover:bg-gray-50 border-b"
                                >
                                  <FaImage size={18} className="text-green-900" />
                                  <span className="flex-1 truncate text-sm">
                                    {file.name}
                                  </span>
                                  <button
                                    onClick={() =>
                                      handleRemoveImage(index, "new")
                                    }
                                    className="p-2 text-red-600 hover:bg-red-50 rounded"
                                  >
                                    <FaTrash size={14} />
                                  </button>
                                </div>
                              ))}
                            </div>
                          </div>
                        </div>

                      </div>
                    )}
                    {totalImages > 0 && (
                      <Button
                        leftIcon={<FaCloudUploadAlt size={18} />}
                        rightIcon={<FaChevronDown size={14} />}
                        variant="outline"
                        border="2px dashed"
                        borderColor="gray.400"
                        color="gray.600"
                        fontSize="sm"
                        fontWeight="medium"
                        bg="white"
                        px={4}
                        py={2}
                        mt={2}
                        h="auto"
                        _hover={{ borderColor: "gray.500", bg: "gray.50" }}
                        onClick={() => fileInputRef.current && fileInputRef.current.click()}
                      >
                        Upload Images
                      </Button>)}
                  </GridItem>
                </div>
              </div>
              <Grid templateColumns={{
                base: "1fr",
                sm: "1fr",
                md: "1fr 1fr",
                lg: "1fr 1fr "
              }}
                gap={5}
                mt={3}
              >
                <GridItem>
                  <FormControl isInvalid={errors.maxWidth}>
                    <FormLabel>
                      Max Width (MM) <span style={{ color: "red" }}>*</span>
                    </FormLabel>
                    <Input
                      name="maxWidth"
                      placeholder="Max Width"
                      value={formData.maxWidth}
                      onChange={handleChange}
                    />
                    <FormErrorMessage fontSize="sm">
                      {errors.maxWidth}
                    </FormErrorMessage>
                  </FormControl>

                </GridItem>

                <GridItem>
                  <FormControl isInvalid={errors.tagsId}>
                    <FormLabel>Tags</FormLabel>
                    <AsyncPaginate
                      isMulti
                      loadOptions={loadTags}
                      additional={{ page: 1 }}
                      debounceTimeout={300}
                      placeholder="Search Tags"
                      value={formData.tagsId || []}
                      onChange={(selectedOptions) => {
                        setFormData({
                          ...formData,
                          tagsId: selectedOptions || [],
                        });
                      }}
                    />
                    {errors.tagsId && (
                      <FormErrorMessage>{errors.tagsId}</FormErrorMessage>
                    )}
                  </FormControl>
                </GridItem>

                {/* Unit Field */}
                <GridItem>
                  <FormControl>
                    <FormLabel>Unit</FormLabel>
                    <Select
                      options={unitOptions}
                      name="unit"
                      placeholder="Select Unit"
                      isClearable
                      isDisabled={isSubmitting}
                      onChange={(option) => handleSelectChange("unit", option)}
                      value={unitOptions.find(
                        (option) => option.value === formData.unit
                      )}
                      styles={{
                        control: (base) => ({
                          ...base,
                          cursor: "pointer",
                        }),
                        option: (base) => ({
                          ...base,
                          cursor: "pointer",
                        }),
                      }}
                    />
                  </FormControl>
                </GridItem>

                {/* Available Stock Field */}
                {/* <GridItem>
                  <FormControl isInvalid={errors.availableStock}>
                    <FormLabel>Available Stock</FormLabel>
                    <Input
                      type="text"
                      name="availableStock"
                      placeholder="Enter Available Stock"
                      value={formData.availableStock}
                      onChange={handleChange}
                    />
                    <FormErrorMessage fontSize="sm">
                      {errors.availableStock}
                    </FormErrorMessage>
                  </FormControl>
                </GridItem> */}
                {/* Created By */}
                <GridItem>
                  <FormControl>
                    <FormLabel>Created By</FormLabel>
                    <AsyncPaginate
                      value={selectedCreatedByOption}
                      loadOptions={() => ({ options: [] })}
                      isDisabled={true}
                      placeholder="Created By"
                    />
                  </FormControl>
                </GridItem>

                {/* Modified By */}
                <GridItem>
                  <FormControl>
                    <FormLabel>Modified By</FormLabel>
                    <AsyncPaginate
                      value={selectedModifiedByOption}
                      loadOptions={() => ({ options: [] })}
                      isDisabled={true}
                      placeholder="Modified By"
                    />
                  </FormControl>
                </GridItem>
              </Grid>
            </Box>
            <Box
              mt={6}
              p={{ base: 4, md: 6 }}
              borderWidth="1px"
              borderRadius="lg"
              bg="white"
              boxShadow="sm"
            >
              <Heading size="md" mb={4}>
                Specifications
              </Heading>

              <Grid
                templateColumns={{
                  base: "1fr",
                  md: "1fr 1fr",
                  lg: "1fr 1fr 1fr",
                }}
                gap={4}
              >
                <GridItem>
                  <FormControl isInvalid={errors.fabric}>
                    <FormLabel>
                      Fabric<span style={{ color: "red" }}>*</span>
                    </FormLabel>
                    <Input
                      name="fabric"
                      placeholder="Fabric"
                      value={formData.fabric}
                      onChange={handleChange}
                    />
                    <FormErrorMessage fontSize="sm">
                      {errors.fabric}
                    </FormErrorMessage>
                  </FormControl>
                </GridItem>

                <GridItem>
                  <FormControl>
                    <FormLabel>GSM</FormLabel>
                    <Input
                      type="text"
                      name="gsm"
                      placeholder="GSM"
                      value={formData.gsm}
                      onChange={handleChange}
                    />
                  </FormControl>
                </GridItem>

                <GridItem>
                  <FormControl>
                    <FormLabel>UV Blockage</FormLabel>
                    <Input
                      name="uvBlockage"
                      placeholder="UV Blockage"
                      value={formData.uvBlockage}
                      onChange={handleChange}
                    />
                  </FormControl>
                </GridItem>

                <GridItem>
                  <FormControl>
                    <FormLabel>Green Guard</FormLabel>
                    <Input
                      name="greenGuard"
                      placeholder="Green Guard"
                      value={formData.greenGuard}
                      onChange={handleChange}
                    />
                  </FormControl>
                </GridItem>
                <GridItem>
                  <FormControl isInvalid={!!errors.isPrintingRequired}>
                    <FormLabel>
                      Is Printing Required? <span style={{ color: "red" }}>*</span>
                    </FormLabel>
                    <Select
                      options={isTypeRequiredOptions}
                      name="isPrintingRequired"
                      onChange={(option) =>
                        handleSelectChange("isPrintingRequired", option)
                      }
                      value={isTypeRequiredOptions.find(
                        (option) =>
                          option.value === formData.isPrintingRequired ||
                          option.label === formData.isPrintingRequired,
                      )}
                      placeholder="Select Yes or No"
                      isClearable={true}
                      styles={{
                        control: (base) => ({
                          ...base,
                          cursor: "pointer",
                          borderColor: errors.isPrintingRequired ? "#E53E3E" : base.borderColor,
                          "&:hover": {
                            borderColor: errors.isPrintingRequired ? "#E53E3E" : base.borderColor,
                          },
                        }),
                        option: (base) => ({
                          ...base,
                          cursor: "pointer",
                        }),
                      }}
                    />
                    <FormErrorMessage fontSize="sm">
                      {errors.isPrintingRequired}
                    </FormErrorMessage>
                  </FormControl>
                </GridItem>
              </Grid>
            </Box>
            {/* Sales & Purchase Cards Section - Same as Item Form */}
            <Flex direction={{ base: "column", md: "row" }} gap={4} mt={6}>
              {/* Sales Info Card */}
              <Box flex="1">
                <Box
                  p={{ base: 4, md: 6 }}
                  borderWidth="1px"
                  borderRadius="lg"
                  bg="white"
                  boxShadow="sm"
                >
                  <Heading size="md" mb={4}>
                    Sales Information
                  </Heading>

                  <FormControl isInvalid={errors.sellingPrice} mb={3}>
                    <FormLabel>Selling Price</FormLabel>
                    <Input
                      type="number"
                      name="sellingPrice"
                      placeholder="Enter Selling Price"
                      value={formData.sellingPrice}
                      onChange={handleChange}
                    />
                    <FormErrorMessage fontSize="sm">
                      {errors.sellingPrice}
                    </FormErrorMessage>
                  </FormControl>

                  <FormControl mb={3}>
                    <FormLabel>Selling Account</FormLabel>
                    <Select
                      options={salesAccountOptions}
                      placeholder="Select Selling Account"
                      isClearable
                      value={salesAccountOptions.find(
                        (option) => option.value === formData.sellingAccount
                      )}
                      onChange={(selected) =>
                        handleSelectChange("sellingAccount", selected)
                      }
                      styles={{
                        control: (base) => ({
                          ...base,
                          cursor: "pointer",
                        }),
                        option: (base) => ({
                          ...base,
                          cursor: "pointer",
                        }),
                      }}
                    />
                  </FormControl>

                  <FormControl>
                    <FormLabel>Selling Description</FormLabel>
                    <Textarea
                      rows={4}
                      name="sellingDescription"
                      placeholder="Enter Selling Description"
                      value={formData.sellingDescription}
                      onChange={handleChange}
                    />
                  </FormControl>
                </Box>
              </Box>

              {/* Purchase Info Card */}
              <Box flex="1">
                <Box
                  p={{ base: 4, md: 6 }}
                  borderWidth="1px"
                  borderRadius="lg"
                  bg="white"
                  boxShadow="sm"
                >
                  <Heading size="md" mb={4}>
                    Purchase Information
                  </Heading>

                  <FormControl isInvalid={errors.costPrice} mb={3}>
                    <FormLabel>Cost Price</FormLabel>
                    <Input
                      type="number"
                      name="costPrice"
                      placeholder="Enter Cost Price"
                      value={formData.costPrice}
                      onChange={handleChange}
                    />
                    <FormErrorMessage fontSize="sm">
                      {errors.costPrice}
                    </FormErrorMessage>
                  </FormControl>

                  <FormControl mb={3}>
                    <FormLabel>Purchase Account</FormLabel>
                    <Select
                      options={purchaseAccountOptions}
                      placeholder="Select Purchase Account"
                      isClearable
                      value={purchaseAccountOptions.find(
                        (option) => option.value === formData.purchaseAccount
                      )}
                      onChange={(selected) =>
                        handleSelectChange("purchaseAccount", selected)
                      }
                      styles={{
                        control: (base) => ({
                          ...base,
                          cursor: "pointer",
                        }),
                        option: (base) => ({
                          ...base,
                          cursor: "pointer",
                        }),
                      }}
                    />
                  </FormControl>

                  <FormControl>
                    <FormLabel>Purchase Description</FormLabel>
                    <Textarea
                      rows={4}
                      name="purchaseDescription"
                      placeholder="Enter Purchase Description"
                      value={formData.purchaseDescription}
                      onChange={handleChange}
                    />
                  </FormControl>
                </Box>
              </Box>
            </Flex>



            <Box
              mt={6}
              p={{ base: 4, md: 6 }}
              borderWidth="1px"
              borderRadius="lg"
              bg="white"
              boxShadow="sm"
            >
              {errors.items && (
                <Box
                  mb={4}
                  p={2}
                  bg="red.50"
                  borderRadius="md"
                  border="1px solid"
                  borderColor="red.200"
                >
                  <FormErrorMessage fontSize="sm">
                    {errors.items}
                  </FormErrorMessage>
                </Box>
              )}

              {/* ✅ Two column layout */}
              <Flex direction={{ base: "column", md: "row" }} gap={6}>

                {/* LEFT SIDE → TABLE */}
                <Box flex={1}>
                  <div className="overflow-x-auto">
                    <table className="w-full border-collapse border border-gray-200 mt-4">
                      <thead className="bg-gray-100">
                        <tr>
                          <th className="border p-2 whitespace-nowrap">Mechanism</th>
                          <th className="border p-2 text-center">Action</th>
                        </tr>
                      </thead>

                      <tbody>
                        {formData.items.map((row, index) => (
                          <tr key={index}>
                            <td className="border px-2 py-1">
                              <AsyncPaginate
                                value={
                                  row.productId
                                    ? inventoryAllData.find((o) => o.value === row.productId)
                                    : null
                                }
                                loadOptions={loadMechanismOptions}
                                additional={{ page: 1 }}
                                debounceTimeout={300}
                                placeholder={
                                  !formData.shadeCategory
                                    ? "Please select a category first"
                                    : loadingCategoryItems
                                      ? "Loading items..."
                                      : "Search Item"
                                }
                                onChange={(selected) => handleItemChange(index, selected)}
                                isDisabled={!formData.shadeCategory || loadingCategoryItems}
                                isClearable
                                isLoading={loadingCategoryItems}
                                menuPortalTarget={document.body}
                                styles={{
                                  menuPortal: (base) => ({
                                    ...base,
                                    zIndex: 9999,
                                  }),
                                }}
                              />
                            </td>

                            <td className="border p-2 text-center">
                              <button
                                onClick={() => removeRow(index)}
                                type="button"
                              >
                                <Trash2
                                  className={`${formData.items.length <= 1
                                    ? "text-gray-400"
                                    : "text-red-500"
                                    }`}
                                />
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>

                    <Button
                      mt={4}
                      size="sm"
                      colorScheme="blue"
                      variant="outline"
                      onClick={addNewRow}
                    >
                      + Add New
                    </Button>
                  </div>
                </Box>

                {/* RIGHT SIDE → DESCRIPTION */}
                <Box flex={1}>
                  <FormControl>
                    <FormLabel>Description</FormLabel>
                    <Textarea
                      rows={6}
                      name="description"
                      placeholder="Description"
                      value={formData.description}
                      onChange={handleChange}
                    />
                  </FormControl>
                </Box>

              </Flex>
            </Box>

            {/* Warehouse Details Section */}
            <Box
              mt={6}
              p={{ base: 4, md: 6 }}
              borderWidth="1px"
              borderRadius="lg"
              bg="white"
              boxShadow="sm"
            >
              <div className="flex justify-between items-center mb-3">
                <Heading as="h3" size="md" mb={4}>
                  Warehouse Details
                </Heading>
                <Button
                  colorScheme="blue"
                  onClick={handleAddWarehouseRow}
                  className="bg-[#011638] text-white hover:bg-[#033366]"
                >
                  + Add New
                </Button>
              </div>

              <Box
                overflowX="auto"
                overflowY="auto"
                maxHeight="350px"
                position="relative"
                sx={{
                  '&::-webkit-scrollbar': {
                    width: '8px',
                    height: '8px',
                  },
                  '&::-webkit-scrollbar-track': {
                    background: '#f1f1f1',
                    borderRadius: '4px',
                  },
                  '&::-webkit-scrollbar-thumb': {
                    background: '#888',
                    borderRadius: '4px',
                  },
                  '&::-webkit-scrollbar-thumb:hover': {
                    background: '#555',
                  },
                }}
              >
                <table className="table table-bordered mt-2 min-w-[600px]">
                  <thead
                    style={{
                      position: 'sticky',
                      top: -1,
                      zIndex: 10,
                      backgroundColor: 'white',
                    }}
                  >
                    <tr>
                      <th className="whitespace-nowrap" style={{ width: "10%" }}>S.No.</th>
                      <th className="whitespace-nowrap" style={{ width: "25%" }}>Warehouse Name</th>
                      <th className="whitespace-nowrap" style={{ width: "25%" }}>Opening Stock</th>
                      <th className="whitespace-nowrap" style={{ width: "30%" }}>
                        Opening Stock Value Per Unit
                      </th>
                      <th className="whitespace-nowrap" style={{ width: "10%", textAlign: "center" }}>
                        Action
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {warehouseRows.map((item, index) => (
                      <tr key={index}>
                        <td className="whitespace-nowrap">{index + 1}</td>
                        <td className="whitespace-nowrap">
                          <Select
                            options={warehouseNameOptions}
                            placeholder="Select Warehouse Name"
                            isClearable
                            value={warehouseNameOptions.find(
                              (option) => option.value === item.warehouseName
                            )}
                            onChange={(selected) =>
                              handleWarehouseInputChange(
                                index,
                                "warehouseName",
                                selected ? selected.value : ""
                              )
                            }
                            menuPortalTarget={document.body}
                            styles={{
                              menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                              control: (base) => ({
                                ...base,
                                cursor: "pointer",
                              }),
                              option: (base) => ({
                                ...base,
                                cursor: "pointer",
                              }),
                            }}
                          />
                        </td>
                        <td className="whitespace-nowrap">
                          <Input
                            type="number"
                            className="form-control"
                            placeholder="Enter opening stock"
                            value={item.openingStock}
                            onChange={(e) =>
                              handleWarehouseInputChange(
                                index,
                                "openingStock",
                                e.target.value
                              )
                            }
                          />
                        </td>
                        <td className="whitespace-nowrap">
                          <Input
                            type="number"
                            className="form-control"
                            placeholder="Enter stock value per unit"
                            value={item.openingStockValuePerUnit}
                            onChange={(e) =>
                              handleWarehouseInputChange(
                                index,
                                "openingStockValuePerUnit",
                                e.target.value
                              )
                            }
                          />
                        </td>
                        <td className="text-center whitespace-nowrap">
                          <button
                            onClick={() => handleDeleteWarehouseRow(index)}
                            className="text-red-600 hover:text-red-800"
                            disabled={warehouseRows.length <= 1}
                          >
                            <Trash2 size={20} />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </Box>
            </Box>

          </Box>
        </>
      )
      }
    </Box >
  );
}